import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { User } from '../types';
import { LogOut, AlertCircle } from 'lucide-react';
import './ImpersonationBanner.css';

interface ImpersonationData {
  impersonationToken: string;
  impersonatedUser: User;
  impersonatedAt: string;
  originalAdminTelegramId: number;
  originalAdminToken?: string;
}

/**
 * Banner component displayed when an admin is impersonating a user
 * Shows the impersonated user's information and provides a button to stop impersonation
 */
export function ImpersonationBanner() {
  const navigate = useNavigate();
  const [impersonationData, setImpersonationData] = useState<ImpersonationData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    // Load impersonation data from sessionStorage on component mount
    const storedData = sessionStorage.getItem('impersonationData');
    if (storedData) {
      try {
        const data = JSON.parse(storedData) as ImpersonationData;
        setImpersonationData(data);
        console.log('🔐 ImpersonationBanner: Loaded impersonation data for user:', data.impersonatedUser.fullName);
      } catch (error) {
        console.error('🔐 ImpersonationBanner: Failed to parse impersonation data:', error);
      }
    }
  }, []);

  const handleStopImpersonation = async () => {
    try {
      setIsProcessing(true);
      console.log('🛑 ImpersonationBanner: Stopping impersonation');

      // Get the original admin data from sessionStorage before clearing
      const adminBackupToken = sessionStorage.getItem('adminBackupToken');

      if (adminBackupToken) {
        // Restore the original admin token
        authService.setToken(adminBackupToken);
        console.log('🛑 ImpersonationBanner: Restored admin token from backup');

        // Validate token to ensure it's still valid, which also updates user context
        try {
          await authService.validateToken();
          console.log('🛑 ImpersonationBanner: Token validated, admin context restored');
        } catch (validationError) {
          console.warn('🛑 ImpersonationBanner: Restored token validation failed', validationError);
        }
      } else {
        // If no backup, clear the token and redirect to auth
        authService.clearToken();
        console.log('🛑 ImpersonationBanner: No admin backup token found - cleared auth token');
      }

      // Clear impersonation data from sessionStorage
      sessionStorage.removeItem('impersonationData');
      sessionStorage.removeItem('adminBackupToken');
      setImpersonationData(null);

      console.log('🛑 ImpersonationBanner: Impersonation stopped - redirecting to admin page');

      // Redirect back to admin page
      navigate('/admin', { replace: true });
    } catch (error) {
      console.error('🛑 ImpersonationBanner: Failed to stop impersonation:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Don't render if not impersonating
  if (!impersonationData) {
    return null;
  }

  const impersonatedAtTime = new Date(impersonationData.impersonatedAt).toLocaleTimeString();

  return (
    <div className="impersonation-banner">
      <div className="impersonation-banner-content">
        <div className="impersonation-banner-icon">
          <AlertCircle size={20} />
        </div>
        <div className="impersonation-banner-info">
          <div className="impersonation-banner-title">
            👤 Impersonating: <strong>{impersonationData.impersonatedUser.fullName}</strong>
          </div>
          <div className="impersonation-banner-detail">
            {impersonationData.impersonatedUser.phoneE164.value} • 
            Started at {impersonatedAtTime}
          </div>
        </div>
        <button
          className="impersonation-banner-button"
          onClick={handleStopImpersonation}
          disabled={isProcessing}
          title="Stop impersonating this user and return to admin panel"
        >
          <LogOut size={16} />
          Stop Impersonation
        </button>
      </div>
    </div>
  );
}
